// src/lib/resources/breadcrumbs.ts

/**
 * @fileoverview Pure, synchronous breadcrumb-item derivation for resource routes.
 *
 * Shared by the server-rendered breadcrumb trail (first-pass HTML anchors for
 * crawlers/link equity) and the thin client wrapper (scroll-border effect).
 * Deliberately free of hooks, fetching, and browser APIs so the exact same
 * item list is produced during SSR and client render — no hydration mismatch.
 *
 * Data-backed leaf labels (glossary-term / calculator / analyzer canonical
 * titles) are NOT fetched here: pages already hold that data server-side and
 * thread it in via the `leafLabel` override.
 */

import { GLOSSARY_CATEGORIES } from '~/lib/resources/types/shared/enums'
import { formatText } from '~/lib/utils/formatters'

/** A single breadcrumb trail entry. */
export interface BreadcrumbItem {
  /** Human-readable label for the crumb. */
  label: string
  /** Absolute path the crumb links to. */
  href: string
}

/**
 * Formats a single path segment into a human-readable breadcrumb label.
 *
 * Mirrors the label rules the old client-only component used, so the
 * server-rendered trail is byte-identical to what users saw post-hydration.
 *
 * @param path - A single URL path segment (e.g. `"calculators"`, `"roas"`).
 * @returns The display label for that segment.
 */
export function formatBreadcrumbLabel(path: string): string {
  // Check for glossary categories
  const glossaryCategory = GLOSSARY_CATEGORIES.find(cat => cat.id === path)
  if (glossaryCategory) {
    return glossaryCategory.label
  }

  // Format tool types
  if (path === 'calculators') return 'Calculators'
  if (path === 'analyzers') return 'Analyzers'
  if (path === 'quizzes') return 'Quizzes'
  if (path === 'tools') return 'Tools'
  if (path === 'glossary') return 'Glossary'

  // Use the formatText utility which handles special cases and proper formatting
  return formatText(path.replace(/-/g, ' '))
}

/**
 * Derives the breadcrumb item list for a `/resources/**` pathname.
 *
 * Pure and synchronous — safe to call during SSR, in server components, and
 * in client components alike. Segment labels come from
 * {@link formatBreadcrumbLabel}; the final (current-page) crumb's label can be
 * overridden with `leafLabel` when the caller has richer server-side data
 * (e.g. a glossary term's or calculator's canonical title).
 *
 * The leading `resources` segment is deliberately NOT emitted: `BreadcrumbTrail`
 * already renders a home-icon crumb linking `/resources` (with an accessible
 * "Resources" label), so emitting it here produced two consecutive links to
 * the same target on every `/resources/**` page. Mirrors the blog generator,
 * which slices off its leading `blog` segment (`generateBreadcrumbs` in
 * `src/components/blog/shared/blog-utils.tsx`). Dedupe tracked in #541
 * (deferred from #534 r3540555031).
 *
 * @param pathname - The current pathname (must start with `/resources`).
 * @param leafLabel - Optional canonical label for the last crumb.
 * @returns Ordered breadcrumb items from the first section below `/resources`
 *   down to the current page — an empty array for non-resource paths and for
 *   the `/resources` hub itself (whose only crumb is the trail's home icon).
 */
export function buildResourceBreadcrumbItems(
  pathname: string,
  leafLabel?: string
): BreadcrumbItem[] {
  // Segment-boundary check: `/resources` or `/resources/...` only, so a
  // sibling route that merely shares the prefix (e.g. `/resourcesfoo`) never
  // produces a trail.
  if (pathname !== '/resources' && !pathname.startsWith('/resources/')) {
    return []
  }

  // Drop the leading `resources` segment — the trail's home-icon crumb is the
  // single `/resources` link (see the doc comment above; #541).
  const segments = pathname.split('/').filter(Boolean).slice(1)
  const items: BreadcrumbItem[] = []
  let currentPath = '/resources'

  for (const [index, segment] of segments.entries()) {
    currentPath += `/${segment}`
    const isLeaf = index === segments.length - 1
    items.push({
      label: isLeaf && leafLabel ? leafLabel : formatBreadcrumbLabel(segment),
      href: currentPath
    })
  }

  return items
}
